import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';

/**
 * Tenant UI visibility config.
 *
 * Phase 1 (current): reads flat dot-notation keys from `environment.uiConfig`,
 * baked into the per-tenant build.
 * Phase 2 (later): swap the config source to a login-response / DB payload via
 * `loadConfig()`. The `isVisible()` interface stays identical, so no template,
 * directive, or component using this service needs to change.
 *
 * Key convention: dot-notation, e.g. 'ott.matchCard.scoreHistory'.
 * Missing key = visible (safe default) so new features don't require touching
 * every existing tenant's config.
 */
@Injectable({ providedIn: 'root' })
export class UiConfigService {
  private config: Record<string, any> =
    ((environment as any).uiConfig as Record<string, any>) ?? {};

  /**
   * Phase 2 hook — replace the config source (e.g. from the login response)
   * without touching any consumer. No-op in Phase 1.
   */
  loadConfig(config: Record<string, any> | null | undefined): void {
    this.config = config ?? {};
  }

  /**
   * Value-analog of isVisible: returns a non-boolean config value (e.g. a label
   * key) for `key`, or `fallback` when the key is absent. Used for tenant
   * overrides that carry a value rather than a show/hide flag.
   */
  getValue<T>(key: string, fallback: T): T {
    const val = this.config[key];
    return val === undefined ? fallback : (val as T);
  }

  /**
   * Returns whether a UI element for `key` should be shown.
   * - Exact match wins.
   * - Otherwise, if any ancestor path is explicitly `false`, the element is
   *   hidden (parent-path inheritance) — e.g. 'ott.matchCard' = false hides
   *   'ott.matchCard.scoreHistory' even if that child has no entry.
   * - Default: visible.
   */
  isVisible(key: string): boolean {
    if (!key) {
      return true;
    }
    if (key in this.config) {
      return this.config[key];
    }

    const parts = key.split('.');
    for (let i = parts.length - 1; i > 0; i--) {
      const parentKey = parts.slice(0, i).join('.');
      if (parentKey in this.config && this.config[parentKey] === false) {
        return false;
      }
    }
    return true;
  }
}
